import React from "react";
import { Button, Column, filterMap, Heading, Modal, Row, Tooltip } from "@hex-insights/core";
import { Badge } from "../../../../../Components/Badge";
import { DetailFormModalController } from "../../../../../Components/DetailFormModal";
import { Icon } from "../../../../../Components/Icon";
import { CourseSectionPeriodFilterInput, HomeRoomSectionDetailQuery } from "../../../../../Utilities";
import { AcademicTermFormat, HomeRoomSectionFormat } from "../../../../Format";
import { HomeRoomSectionForm } from "../../../../Forms";
import { AcademicTermLink } from "../../../../Links";
import {
	AcademicTermUtilities,
	StudentHomeRoomSectionEnrollmentUtilities,
	TeacherHomeRoomSectionEnrollmentUtilities,
} from "../../../../Utilities";
import * as CourseSectionPeriodComponents from "../../../CourseSectionPeriod";
import * as EmployeeComponents from "../../../Employee";
import * as StudentComponents from "../../../Student";
import { HomeRoomSectionIcon } from "../../Icon";
import styles from "./styles.module.css";

export type HeaderProps = {
	homeRoomSection: HomeRoomSectionDetailQuery["homeRoomSection"];
};

export function Header({ homeRoomSection }: HeaderProps) {
	return (
		<Row justify="space-between" align="center" className={styles["header"]}>
			<Column justify="spaced-start">
				<Row justify="spaced-start" align="center">
					<Heading.H1 noMargin>{HomeRoomSectionFormat.name(homeRoomSection)}</Heading.H1>
					<Tooltip.Container>
						<Tooltip>
							<Tooltip.Body>
								<span className="nowrap">
									{AcademicTermFormat.Fields.startDate(homeRoomSection.academicTerm.startDate)} to{" "}
									{AcademicTermFormat.Fields.endDate(homeRoomSection.academicTerm.endDate)}
								</span>
							</Tooltip.Body>
						</Tooltip>

						<AcademicTermLink instance={homeRoomSection.academicTerm} className="link--no-text-decoration">
							<Badge className={styles["header__badge"]}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									{AcademicTermFormat.name(homeRoomSection.academicTerm)}
								</Row>
							</Badge>
						</AcademicTermLink>
					</Tooltip.Container>

					<ScheduleController homeRoomSection={homeRoomSection} />
				</Row>

				<Row justify="spaced-start" horizontalSpacing="1.5rem">
					<TeachersView homeRoomSection={homeRoomSection} />
					<StudentsList homeRoomSection={homeRoomSection} />
				</Row>
			</Column>

			<HomeRoomSectionIcon
				homeRoomSection={homeRoomSection}
				size="5rem"
				className={styles["header__home-room-section-icon"]}
			/>

			<div className={styles["header__edit-button__container"]}>
				<DetailFormModalController
					heading="Edit Home Room Section"
					DetailForm={HomeRoomSectionForm.ControlledDetail}
					instanceName="homeRoomSection"
					instance={homeRoomSection}
					className={styles["header__edit-button"]}
				/>
			</div>
		</Row>
	);
}

type ScheduleControllerProps = {
	homeRoomSection: HomeRoomSectionDetailQuery["homeRoomSection"];
};

function ScheduleController({ homeRoomSection }: ScheduleControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const initialDate = AcademicTermUtilities.isActive(homeRoomSection.academicTerm)
		? undefined
		: new Date(homeRoomSection.academicTerm.startDate);

	const filters = React.useMemo<CourseSectionPeriodFilterInput>(
		() => ({
			courseSection: [{ homeRoomSectionIDEQ: homeRoomSection.id }],
		}),
		[homeRoomSection.id],
	);

	return (
		<React.Fragment>
			<Button onClick={openModal} style={{ padding: "0", border: "none", borderRadius: 0, cursor: "pointer" }}>
				<Badge className={styles["header__badge"]}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.CalendarBlank size={16} /> View Schedule
					</Row>
				</Badge>
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal size="large" onClose={closeModal}>
					<Modal.Body style={{ height: "100%" }}>
						<CourseSectionPeriodComponents.ScheduleCalendar
							initialDate={initialDate}
							filters={filters}
							style={{ height: "100%" }}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type TeachersViewProps = {
	homeRoomSection: HomeRoomSectionDetailQuery["homeRoomSection"];
};

function TeachersView({ homeRoomSection }: TeachersViewProps) {
	if (homeRoomSection.teacherHomeRoomSectionEnrollments.length === 0) {
		return null;
	}

	const activeTeachers = filterMap(
		homeRoomSection.teacherHomeRoomSectionEnrollments,
		(e) => TeacherHomeRoomSectionEnrollmentUtilities.isActive(e),
		(e) => e.teacher,
	);

	return <EmployeeComponents.EmployeesCondensedListButton label="Teacher" employees={activeTeachers} />;
}

type StudentsListProps = {
	homeRoomSection: HomeRoomSectionDetailQuery["homeRoomSection"];
};

function StudentsList({ homeRoomSection }: StudentsListProps) {
	if (homeRoomSection.studentHomeRoomSectionEnrollments.length === 0) {
		return null;
	}

	const activeStudents = filterMap(
		homeRoomSection.studentHomeRoomSectionEnrollments,
		(e) => StudentHomeRoomSectionEnrollmentUtilities.isActive(e),
		(e) => e.student,
	);

	return <StudentComponents.StudentsCondensedListButton students={activeStudents} />;
}
